import { memo, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCommunityStats } from "@/hooks/use-community-stats";
import SectionBackground from "../layout/SectionBackground";
import Tile from "../shared/Tile";
import TileGrid from "../shared/TileGrid";
import { Award, Globe2, Users } from "lucide-react";
import { theme } from "@/theme";

const About = () => {
  const ref = useRef(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const communityStats = useCommunityStats();

  // Scroll-triggered animations
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Memoize animation transforms for performance
  const animationConfigs = useMemo(() => ({
    mobile: isMobile,
    titleRange: isMobile ? [1, 1] : [0, 1],
    titleYRange: isMobile ? [0, 0] : [20, 0],
    contentRange: isMobile ? [1, 1] : [0, 1],
    contentYRange: isMobile ? [0, 0] : [15, 0],
    exitRange: isMobile ? [1, 1] : [1, 0],
    exitYRange: isMobile ? [0, 0] : [0, 30],
  }), [isMobile]);

  // Entry animations - simplified (disabled on mobile to prevent dark overlay effect)
  const titleOpacity = useTransform(scrollYProgress, [0, 0.2], animationConfigs.titleRange);
  const titleY = useTransform(scrollYProgress, [0, 0.2], animationConfigs.titleYRange);
  const contentOpacity = useTransform(scrollYProgress, [0.1, 0.3], animationConfigs.contentRange);
  const contentY = useTransform(scrollYProgress, [0.1, 0.3], animationConfigs.contentYRange);
  const glowY = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [50, -30]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 1], isMobile ? [0, 0, 0, 0] : [0, 0.3, 0.2, 0.1]);

  // Exit animations - disabled on mobile (prevents dark fade effect)
  const exitOpacity = useTransform(scrollYProgress, [0.8, 0.95], animationConfigs.exitRange);
  const exitY = useTransform(scrollYProgress, [0.8, 0.95], animationConfigs.exitYRange);
  const exitScale = useTransform(scrollYProgress, [0.8, 0.95], isMobile ? [1, 1] : [1, 0.98]);

  // Background and filter transforms (must be at top level, not inside conditional JSX)
  const bgOpacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], isMobile ? [0, 0, 0, 0] : [0, 0.4, 0.4, 0.2]);
  const contentFilter = useTransform(scrollYProgress, [0, 0.15], isMobile ? ["blur(0px)", "blur(0px)"] : ["blur(2px)", "blur(0px)"]);

  // Lazy load and play video when section comes into view
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // Only load video when in viewport for performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load video source if not already loaded
            if (!videoElement.src && videoElement.dataset.src) {
              videoElement.src = videoElement.dataset.src;
            }
            videoElement.play().catch(() => {
              // Video autoplay prevented
            });
            observer.unobserve(videoElement);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(videoElement);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="relative pt-8 md:pt-16 px-4 pb-12 md:pb-20 overflow-hidden">
      <SectionBackground />

      {/* Enhanced background with theme colors - Disabled on mobile */}
      {!isMobile && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ opacity: bgOpacity }}
        >
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full md:blur-[120px] blur-[80px]"
          style={{
            background: `radial-gradient(circle, ${theme.colors.accent}30, transparent 70%)`,
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full md:blur-[100px] blur-[60px]"
          style={{
            background: `radial-gradient(circle, ${theme.colors.accentLight}20, transparent 70%)`,
          }}
        />
        </motion.div>
      )}

      {/* Dynamic accent beam - Disabled on mobile */}
      {!isMobile && (
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 w-[500px] h-[300px] pointer-events-none md:blur-[60px] blur-[30px]"
          style={{
            y: glowY,
            opacity: glowOpacity,
            background: `radial-gradient(ellipse 60% 80% at 50% 50%, ${theme.colors.accent}40 0%, ${theme.colors.accentDark}20 40%, transparent 70%)`,
            mixBlendMode: "screen",
          }}
        />
      )}

      <motion.div 
        className="relative max-w-7xl mx-auto"
        style={{
          opacity: exitOpacity,
          y: exitY,
          scale: exitScale,
        }}
      >
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Video/Image Side */}
          <motion.div
            className="relative"
            style={{
              opacity: contentOpacity,
              y: contentY,
              filter: contentFilter,
            }}
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden relative group">
              <video
                ref={videoRef}
                data-src="https://media.klauyros.artoverse.club/homepageintro.webm"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loop
                muted
                playsInline
                preload="none"
                poster=""
              />
              {/* Enhanced border effect */}
              <div 
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  border: `1px solid ${theme.colors.accent}40`,
                  boxShadow: `inset 0 0 60px ${theme.colors.accent}10`,
                }}
              />
              {/* Theme-based glow */}
              <div 
                className="absolute -inset-6 blur-3xl -z-10 opacity-60"
                style={{
                  background: `radial-gradient(circle at center, ${theme.colors.accent}40, transparent 70%)`,
                }}
              />
            </div>
          </motion.div>

          {/* Text Side */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {/* Section label */}
              <div 
                className="inline-block px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wider uppercase"
                style={{
                  backgroundColor: `${theme.colors.accent}20`,
                  color: theme.colors.accent,
                  border: `1px solid ${theme.colors.accent}40`,
                }}
              >
                About Us
              </div>

              <h2
                className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                style={{ color: theme.colors.text }}
              >
                Connection Through{" "}
                <span 
                  className="inline-block relative"
                  style={{ color: theme.colors.accent }}
                >
                  Movement
                  <motion.div
                    className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                    style={{ backgroundColor: theme.colors.accent }}
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  />
                </span>
              </h2>
            </motion.div>

            <motion.p
              className="text-lg md:text-xl leading-relaxed mb-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.5,
                delay: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              style={{ color: theme.colors.secondary }}
            >
              From the social floor to international stages, Klau y Ros bring connection, artistry, and emotion to every
              move.
            </motion.p>

            <motion.p
              className="text-base md:text-lg leading-relaxed mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.5,
                delay: 0.4,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              style={{ color: theme.colors.textMuted }}
            >
              Their approach combines technical mastery with authentic expression, creating a dance experience that
              transforms both partners and connects them to the heart of Bachata.
            </motion.p>

            {/* Stats using Tile components */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.5,
                delay: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <TileGrid columns={3} gap="md">
                <Tile
                  variant="glass"
                  size="sm"
                  hoverable
                  noPadding
                  className="text-center"
                >
                  <div className="p-4">
                    <Award size={28} style={{ color: theme.colors.accent }} className="mx-auto mb-3" />
                    <div className="text-3xl font-bold mb-1" style={{ color: theme.colors.accent }}>
                      10+
                    </div>
                    <div className="text-xs uppercase tracking-wide" style={{ color: theme.colors.textMuted }}>
                      Years Teaching
                    </div>
                  </div>
                </Tile>

                <Tile
                  variant="glass"
                  size="sm"
                  hoverable
                  spotlight
                  noPadding
                  className="text-center"
                >
                  <div className="p-4">
                    <Globe2 size={28} style={{ color: theme.colors.accent }} className="mx-auto mb-3" />
                    <div className="text-3xl font-bold mb-1" style={{ color: theme.colors.accent }}>
                      {communityStats.loading ? "..." : `${communityStats.countries}+`}
                    </div>
                    <div className="text-xs uppercase tracking-wide" style={{ color: theme.colors.textMuted }}>
                      Countries
                    </div>
                  </div>
                </Tile>

                <Tile
                  variant="glass"
                  size="sm"
                  hoverable
                  noPadding
                  className="text-center"
                >
                  <div className="p-4">
                    <Users size={28} style={{ color: theme.colors.accent }} className="mx-auto mb-3" />
                    <div className="text-3xl font-bold mb-1" style={{ color: theme.colors.accent }}>
                      {communityStats.loading ? "..." : `${communityStats.members.toLocaleString()}+`}
                    </div>
                    <div className="text-xs uppercase tracking-wide" style={{ color: theme.colors.textMuted }}>
                      Students
                    </div>
                  </div>
                </Tile>
              </TileGrid>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default memo(About);
